import { ArrowRight, Check, Globe2, ShieldCheck } from "lucide-react";
import { track } from "@/lib/analytics";
import { globalGrowthPage, globalGrowthPages, type GlobalGrowthPage as GlobalPageData } from "@/marketing/growth/globalCatalog";
import { GrowthHero } from "@/marketing/growth/ToolPage";

const GROUP_LABEL: Record<GlobalPageData["group"], string> = {
  solution: "SOLUTION",
  role: "ROLE",
  market: "MARKET",
  industry: "INDUSTRY",
  index: "GLOBAL DIRECTORY",
};

export function GlobalGrowthPage({ path }: { path: string }) {
  const page = globalGrowthPage(path);
  if (!page) {
    return (
      <GrowthHero eyebrow="PAGE NOT FOUND" title="This global growth page does not exist." text="Browse the published RAQOZ solution, market and role pages.">
        <a className="marketing-button-primary-light mt-8" href="/solutions">Browse solutions</a>
      </GrowthHero>
    );
  }

  const siblingPages = globalGrowthPages.filter((item) => item.group === page.group && item.path !== page.path);
  const earlyAccess = page.taxonomy.publicationState === "early-access";
  const marketPartnerLinks = page.group === "market" ? [["For CAs and advisors", "/for-chartered-accountants"], ["For ISO consultants", "/for-iso-consultants"], ["Founding Controls Circle", "/founding-circle"], ["Procurement Controls Kit", "/partners/procurement-controls-kit"]] as const : [];

  return (
    <GrowthHero eyebrow={page.eyebrow + " · " + GROUP_LABEL[page.group]} title={page.title} text={page.thesis}>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <span className={"inline-flex rounded-md px-3 py-1.5 text-xs font-black tracking-[.1em] " + (earlyAccess ? "bg-status-beta-bg text-status-beta-on-light" : "bg-status-available-bg text-teal-deep")}>
          {earlyAccess ? "EARLY ACCESS" : "PUBLISHED"}
        </span>
        <span className="text-sm text-on-paper-muted">Last reviewed {page.taxonomy.lastVerified}</span>
      </div>

      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {page.sections.map(([heading, body]) => (
          <article className="rounded-[1.5rem] border border-line-light bg-surface-card p-6" key={heading}>
            <span className="grid size-10 place-items-center rounded-2xl bg-teal-wash text-teal-deep"><Check className="size-5" /></span>
            <h2 className="mt-5 font-serif text-2xl">{heading}</h2>
            <p className="mt-3 text-sm leading-6 text-on-paper-muted">{body}</p>
          </article>
        ))}
      </div>

      {page.links && page.links.length > 0 && (
        <section className="mt-10 rounded-[1.7rem] border border-line-light bg-surface-card p-7">
          <p className="marketing-eyebrow-light">CONTINUE WITH THE RELEVANT WORKFLOW</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {page.links.map(([label, href]) => (
              <a className="flex min-h-14 items-center justify-between rounded-xl border border-line-light px-5 py-3 font-bold transition-colors hover:bg-teal-wash focus-visible:bg-teal-wash" href={href} key={href}>
                {label}<ArrowRight className="size-4 shrink-0 text-teal-deep" />
              </a>
            ))}
          </div>
        </section>
      )}

      {marketPartnerLinks.length > 0 && <section className="mt-10 rounded-[1.7rem] border border-line-light bg-surface-card p-7"><p className="marketing-eyebrow-light">ADVISOR AND EARLY-ACCESS PATHS</p><div className="mt-6 grid gap-3 sm:grid-cols-3">{marketPartnerLinks.map(([label, href]) => <a className="marketing-text-link-light justify-between" href={href} key={href}>{label}<ArrowRight className="size-4" /></a>)}</div></section>}

      <section className="mt-10 grid gap-6 rounded-[2rem] bg-surface-ink p-8 text-on-ink-strong lg:grid-cols-[1fr_.75fr] lg:p-10">
        <div>
          <p className="marketing-eyebrow-dark">{earlyAccess ? "STAGED COUNTRY ACTIVATION" : "THE RAQOZ CONTINUATION"}</p>
          <h2 className="mt-4 font-serif text-4xl">{page.ctaLabel}</h2>
          <p className="mt-4 max-w-2xl leading-7 text-on-ink">{page.description}</p>
          <a
            className="marketing-button-primary-dark mt-7"
            href={page.ctaHref}
            onClick={() => track("cta_click", { source: page.path.slice(0, 100), cta: page.ctaLabel.slice(0, 80) })}
          >
            {page.ctaLabel}<ArrowRight className="size-4" />
          </a>
        </div>
        <aside className="rounded-[1.5rem] border border-teal-border bg-surface-ink p-6">
          <div className="flex gap-3">
            {page.group === "market" || page.path === "/markets"
              ? <Globe2 className="mt-1 size-5 shrink-0 text-teal-bright" />
              : <ShieldCheck className="mt-1 size-5 shrink-0 text-teal-bright" />}
            <div>
              <p className="text-xs font-black tracking-[.12em] text-teal-bright">CLAIM BOUNDARY</p>
              <p className="mt-3 text-sm leading-6 text-on-ink">{page.boundary}</p>
            </div>
          </div>
        </aside>
      </section>

      {page.group !== "index" && siblingPages.length > 0 && (
        <nav aria-label={"More " + page.group + " pages"} className="mt-10">
          <p className="marketing-eyebrow-light">EXPLORE RELATED {page.group.toUpperCase()} PAGES</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {siblingPages.slice(0, 6).map((item) => (
              <a className="marketing-button-secondary-light" href={item.path} key={item.path}>{item.title}</a>
            ))}
          </div>
        </nav>
      )}
    </GrowthHero>
  );
}
